import { api, ApiError } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import { isAuthenticated } from "../core/tokens.js";
import { navigate, onRouteLeave } from "../core/router.js";
import { t } from "../core/i18n.js";
import type { Variant } from "../models.js";

/**
 * Lightweight region editor (04-web §2.5, 00 §3.4 fork→edit→commit, 01 §3.D).
 * Pick an arbitrary [start_ms, end_ms) region (number inputs + a visual range bar) →
 * POST /v1/episodes/:id/variants (fork) → POST /v1/variants/:id/publish (commit) →
 * poll GET /v1/variants/:id/jobs for state, with a fork→publish→process stepper + progress.
 * AI label (ai_generated / ai_edited / deepfake) is required before publish (00 §7.3).
 */

interface JobsResponse {
  jobs: Array<{ id: string; type: string; state: string }>;
  status?: string;
}

type Step = "region" | "fork" | "publish" | "process";
const TERMINAL = ["succeeded", "failed", "dead"];

export function editorView(params: Record<string, string>, root: HTMLElement): void {
  if (!isAuthenticated()) {
    navigate("/login");
    return;
  }
  const episodeId = params.episodeId ?? "";

  if (!episodeId) {
    mount(
      root,
      h(
        "section",
        { style: "max-width:560px;margin:0 auto" },
        h("h1", {}, t("editor.title")),
        h("p", { class: "error" }, t("editor.missingEpisode")),
      ),
    );
    return;
  }

  // --- region picker -------------------------------------------------------
  const totalInput = h("input", { class: "field", type: "number", value: "60000", min: "1" }) as HTMLInputElement;
  const startInput = h("input", { class: "field", type: "number", value: "0", min: "0" }) as HTMLInputElement;
  const endInput = h("input", { class: "field", type: "number", value: "3000", min: "1" }) as HTMLInputElement;
  const labelText = h("input", { class: "field", placeholder: t("editor.labelPlaceholder") }) as HTMLInputElement;

  const rangeFill = h("div", { class: "range-fill" }) as HTMLDivElement;
  const rangeBar = h("div", { class: "range-bar" }, rangeFill) as HTMLDivElement;
  const rangeLabel = h("p", { class: "muted", style: "font-size:13px;margin:6px 0 0" }, "") as HTMLParagraphElement;

  const refreshRange = (): void => {
    const total = Math.max(1, Number(totalInput.value) || 1);
    const start = Math.max(0, Number(startInput.value) || 0);
    const end = Math.max(0, Number(endInput.value) || 0);
    const lo = Math.min(start, total);
    const hi = Math.min(Math.max(end, lo), total);
    rangeFill.style.left = `${(lo / total) * 100}%`;
    rangeFill.style.width = `${((hi - lo) / total) * 100}%`;
    rangeLabel.textContent = t("editor.selectedRange", { start: lo, end: hi, dur: Math.max(0, hi - lo) });
  };
  for (const el of [totalInput, startInput, endInput]) el.addEventListener("input", refreshRange);

  // --- AI labels (required before publish) ---------------------------------
  const aiGenerated = h("input", { type: "checkbox" }) as HTMLInputElement;
  const aiEdited = h("input", { type: "checkbox", checked: true }) as HTMLInputElement;
  const deepfake = h("input", { type: "checkbox" }) as HTMLInputElement;

  // --- state / progress chrome ---------------------------------------------
  const status = h("p", { class: "muted" }, "");
  const progressFill = h("div", { class: "progress-fill" }) as HTMLDivElement;
  const progressBar = h("div", { class: "progress-bar", style: "display:none" }, progressFill) as HTMLDivElement;
  const jobsBox = h("div", { class: "card", style: "padding:12px;margin-top:12px;display:none;cursor:default" }) as HTMLDivElement;

  const steps: Record<Step, HTMLElement> = {
    region: stepChip(t("editor.step.region")),
    fork: stepChip(t("editor.step.fork")),
    publish: stepChip(t("editor.step.publish")),
    process: stepChip(t("editor.step.process")),
  };
  const stepper = h("div", { class: "stepper" }, steps.region, steps.fork, steps.publish, steps.process);
  const setStep = (active: Step, done: Step[] = []): void => {
    for (const k of Object.keys(steps) as Step[]) {
      steps[k].className = `step-chip${k === active ? " active" : ""}${done.includes(k) ? " done" : ""}`;
    }
  };

  const forkBtn = h("button", { class: "btn", style: "width:100%;margin-top:12px" }, t("editor.fork")) as HTMLButtonElement;
  const publishBtn = h(
    "button",
    { class: "btn secondary", style: "width:100%;margin-top:8px", disabled: true },
    t("editor.publish"),
  ) as HTMLButtonElement;

  let variantId: string | null = null;
  let pollTimer: number | null = null;

  const setStatus = (text: string, cls = "muted"): void => {
    status.textContent = text;
    status.className = cls;
  };

  const fork = async (): Promise<void> => {
    const startMs = Number(startInput.value);
    const endMs = Number(endInput.value);
    if (!(endMs > startMs) || startMs < 0) {
      return setStatus(t("editor.invalidRange"), "error");
    }
    forkBtn.disabled = true;
    try {
      setStatus(t("editor.forking"));
      const variant = await api.post<Variant>(`/v1/episodes/${episodeId}/variants`, {
        start_ms: startMs,
        end_ms: endMs,
        label: labelText.value.trim() || null,
        source: "edit",
      });
      variantId = variant.id;
      publishBtn.disabled = false;
      setStep("publish", ["region", "fork"]);
      setStatus(t("editor.forked", { id: variant.id }), "muted");
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
      setStatus(t("editor.forkFailed", { msg }), "error");
    } finally {
      forkBtn.disabled = false;
    }
  };

  const publish = async (): Promise<void> => {
    if (!variantId) return;
    if (!aiGenerated.checked && !aiEdited.checked && !deepfake.checked) {
      return setStatus(t("compliance.labelRequiredPublish"), "error");
    }
    publishBtn.disabled = true;
    try {
      setStatus(t("editor.publishing"));
      await api.post(`/v1/variants/${variantId}/publish`, {
        label: {
          ai_generated: aiGenerated.checked,
          ai_edited: aiEdited.checked,
          deepfake: deepfake.checked,
        },
      });
      setStep("process", ["region", "fork", "publish"]);
      setStatus(t("editor.processing"), "muted");
      jobsBox.style.display = "block";
      progressBar.style.display = "block";
      startPolling();
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
      setStatus(t("editor.publishFailed", { msg }), "error");
      publishBtn.disabled = false;
    }
  };

  const poll = async (): Promise<void> => {
    if (!variantId) return;
    try {
      const res = await api.get<JobsResponse>(`/v1/variants/${variantId}/jobs`);
      const jobs = res.jobs ?? [];
      const rows = jobs.map((j) =>
        h(
          "div",
          { style: "display:flex;gap:10px;font-size:13px;margin:4px 0" },
          h("span", {}, j.type),
          h("span", { class: "muted" }, j.state),
        ),
      );
      const done = jobs.filter((j) => TERMINAL.includes(j.state)).length;
      const total = jobs.length;
      progressFill.style.width = total ? `${(done / total) * 100}%` : "0%";
      jobsBox.replaceChildren(
        h("strong", {}, t("editor.jobs")),
        h("p", { class: "muted", style: "font-size:12px;margin:4px 0 8px" }, t("editor.progress", { done, total })),
        ...rows,
      );
      const terminal = total > 0 && done === total;
      if (res.status === "published" || terminal) {
        stopPolling();
        progressFill.style.width = "100%";
        setStatus(res.status === "published" ? t("editor.published") : t("editor.pipelineDone"), "muted");
      }
    } catch (e) {
      const msg = e instanceof ApiError ? `${e.status} ${e.code}` : String(e);
      jobsBox.replaceChildren(h("span", { class: "error" }, t("editor.jobsFailed", { msg })));
      stopPolling();
    }
  };

  const startPolling = (): void => {
    stopPolling();
    void poll();
    pollTimer = window.setInterval(() => void poll(), 3000);
  };
  const stopPolling = (): void => {
    if (pollTimer !== null) {
      window.clearInterval(pollTimer);
      pollTimer = null;
    }
  };
  onRouteLeave(stopPolling); // stop polling when navigating away (link or back/forward)

  forkBtn.addEventListener("click", () => void fork());
  publishBtn.addEventListener("click", () => void publish());

  setStep("region");
  refreshRange();

  mount(
    root,
    h(
      "section",
      { style: "max-width:560px;margin:0 auto" },
      h("h1", {}, t("editor.title")),
      h("p", { class: "muted" }, t("editor.intro", { id: episodeId })),
      stepper,
      h("h3", {}, t("editor.region")),
      labeled(t("editor.total"), totalInput),
      labeled(t("editor.start"), startInput),
      labeled(t("editor.end"), endInput),
      rangeBar,
      rangeLabel,
      labeled(t("editor.labelField"), labelText),
      h("h3", {}, t("compliance.labelHeading")),
      h(
        "div",
        { class: "card", style: "padding:14px;margin:8px 0;cursor:default" },
        check(aiGenerated, t("compliance.aiGenerated")),
        check(aiEdited, t("compliance.aiEdited")),
        check(deepfake, t("compliance.deepfake")),
      ),
      forkBtn,
      publishBtn,
      status,
      progressBar,
      jobsBox,
    ),
  );
}

function stepChip(label: string): HTMLElement {
  return h("span", { class: "step-chip" }, label);
}

function check(input: HTMLInputElement, label: string): HTMLElement {
  return h("label", { style: "display:flex;gap:8px;align-items:center;margin:6px 0;font-size:14px" }, input, h("span", {}, label));
}

function labeled(label: string, field: HTMLElement): HTMLElement {
  return h("label", { style: "display:block;margin:8px 0" }, h("span", { class: "muted", style: "font-size:13px" }, label), field);
}
